/* eslint-disable jsx-a11y/no-static-element-interactions */
import { useRecoilValue } from 'recoil';
import { get } from 'lodash';

import * as T from 'common/types';

import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

import { plotsStylesState } from 'styles/stylesProvider';
import './TraceSpan.scss';

type TraceSpanProps = {
  span: T.Span;
  traceStartTime: number;
  traceDuration: number;
  detailsVisible: boolean;
  setSpanSelected: (spanId: string) => void;
};

const rightArrow = <ArrowRightIcon fontSize="small" />;
const downArrow = <ArrowDropDownIcon fontSize="small" />;

const renderTags = (tags?: Record<string, string>): JSX.Element | null => {
  if (!tags) {
    return null;
  }

  // TODO: Need to generate the url by updating current applied filter to include this tag value.
  const tagsList = Object.keys(tags).map((key) => (
    <div className="TraceSpan__TagPairs">
      <span className="TraceSpan__TagKey">{key} = </span>
      <span className="TraceSpan__TagValue">{tags[key]}</span>
    </div>
  ));
  return (
    <div className="TraceSpan__Tags">
      <div className="TraceSpan__TagsTitle">Tags:</div>
      <div className="TraceSpan__TagsContainer">{tagsList}</div>
    </div>
  );
};

const renderLogFields = (log: T.SpanLogs): Array<JSX.Element> => {
  const fields = log.fields;
  // TODO: Need to generate the url by updating current applied filter to include this field value.
  const logFields = Object.keys(log.fields).map((key) => (
    <div className="TraceSpan__LogFieldsPairs">
      <span>{key} = </span>
      <span>{fields[key]}</span>
    </div>
  ));
  return logFields;
};

const renderLogs = (spanStartTime: number, logs?: Array<T.SpanLogs>): Array<JSX.Element> | null => {
  if (!logs) {
    return null;
  }
  const logsList = logs.map((log) => (
    <div className="TraceSpan__LogTitle">
      <div className="TraceSpan__LogTimestamp">{log.timestamp - spanStartTime}</div>
      <div className="TraceSpan__LogFieldsContainer">{renderLogFields(log)}</div>
    </div>
  ));
  return logsList;
};

/**
 * This component draws a line of waterfall view for Trace Details. Each span in Trace is converted a Span line,
 * which consist of details about the span and a visual representation of the duration of the span. It also provides
 * a more detail view, visible on click, to show more details such as tags, logs on user click.
 *
 * @params - span             - Span we're the drawing the waterfall line for
 * @params - traceStartTime   - Start time of overall trace this span is enclosed in.
 * @params - traceDuration    - Duration of overall trace.
 * @params - detailsVisible   - Whether to show details or not. This state is handled in parent because we want to hide
 *                              details of other spans when a span is selected.
 * @params - setSpanSelected -  Set a span as selected i.e. show it's details. This just sets the state in parent and hide other spans.
 */
const TraceSpan = ({
  span,
  traceStartTime,
  traceDuration,
  detailsVisible,
  setSpanSelected,
}: TraceSpanProps): JSX.Element => {
  const plotsStyles = useRecoilValue(plotsStylesState);
  const serverHost = get(span, 'serverInfo.serverHost');
  const podName = get(span, 'serverInfo.podName');
  const timeSinceStart = span.startTime - traceStartTime;
  const arrow = detailsVisible ? downArrow : rightArrow;
  let service;
  let component;
  if (span.service) {
    service = (
      <span>
        <span>Service:</span>
        <span>{span.service}</span>
        <span>|</span>
      </span>
    );
  }
  if (span.component) {
    service = (
      <span>
        <span>Component:</span>
        <span>{span.component}</span>
        <span>|</span>
      </span>
    );
  }
  const widthUnit = `100% / ${traceDuration}`;
  const spanDuration = span.startTime > 0 && span.endTime > 0 ? span.endTime - span.startTime : -1;
  const spanLineStyle = {
    backgroundColor: plotsStyles[span.level],
    width: `calc(${widthUnit} * ${spanDuration})`,
    marginLeft: `calc(${widthUnit} * ${timeSinceStart})`,
  };

  return (
    <div className="TraceSpan">
      <div className="TraceSpan__SpanContainer">
        <div
          className="TraceSpan__SpanTitle"
          onClick={(): void => {
            setSpanSelected(span.spanId);
          }}
          style={{ paddingLeft: `calc((20px * ${span.level}) + 10px)` }}
        >
          {arrow}
          <span className="TraceSpan__OperationName">{span.operation || 'Operation undefined'}</span>
          <span className="TraceSpan__ServerHost">{serverHost}</span>
          <span className="TraceSpan__PodName">{podName}</span>
        </div>
        <div className="TraceSpan__SpanLineContainer">
          <div className="TraceSpan__SpanLine" style={spanLineStyle} />
        </div>
      </div>
      <div
        className={detailsVisible ? 'TraceSpan__SpanDetails' : 'TraceSpan__SpanDetails--hidden'}
        style={{ paddingLeft: `calc((20px * ${span.level}) + 34px)` }}
      >
        <div className="TraceSpan__DetailsTitleBar">
          <div className="TraceSpan__DetailsOperationTitle">
            <span className="TraceSpan__DetailsOperationName">{span.operation}</span>
            <span className="TraceSpan__DetailsTimeSinceStart">{timeSinceStart} ms</span>
          </div>
          <div className="TraceSpan__Operation">
            {service}
            {component}
          </div>
        </div>
        <div className="TraceSpan__DetailsContent">
          {renderTags(span.tags)}
          {renderLogs(span.startTime, span.logs)}
        </div>
      </div>
    </div>
  );
};

export default TraceSpan;
